import React from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSidebar } from "@/contexts/SidebarContext";
import { ChevronDoubleRightIcon } from "@/components/Icons/ChevronDoubleRightIcon";
import { ChevronDoubleLeftIcon } from "@/components/Icons/ChevronDoubleLeftIcon";
import { TenantHeaderTabs } from "./TenantHeaderTabs";
import { useNavbarNavigation } from "./hooks/useNavbarNavigation";
import { NotificationsTrigger } from "./NotificationsTrigger";
import TrainIcon from "@/assets/images/logos/train.svg";
import GraphIcon from "@/assets/images/logos/graph.svg";
import IndifferentGhostIcon from "@/assets/images/logos/indifferent-ghost.svg";

/**
 * Generates unit navigation items dynamically based on the unit ID
 * @param unitId - The current unit identifier from URL params
 */
const getUnitNavigationItems = (unitId: string) => [
  {
    name: "details",
    path: `/units/${unitId}`,
    translationKey: "unit.navigation.details",
    icon: <img src={TrainIcon} alt="Train" className="w-5 h-5" />,
    disabled: false,
  },
  {
    name: "graph",
    path: `/units/${unitId}/graph`,
    translationKey: "unit.navigation.graph",
    icon: <img src={GraphIcon} alt="Graph" className="w-5 h-5" />,
    disabled: false,
  },
  {
    name: "export",
    path: `/units/${unitId}/export`,
    translationKey: "unit.navigation.export",
    icon: <img src={IndifferentGhostIcon} alt="Export" className="w-5 h-5" />,
    disabled: true,
  },
  {
    name: "diagnostics",
    path: `/units/${unitId}/diagnostics`,
    translationKey: "unit.navigation.diagnostics",
    icon: (
      <img src={IndifferentGhostIcon} alt="Diagnostics" className="w-5 h-5" />
    ),
    disabled: true,
  },
];

export const TenantSidebar: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { isCollapsed, toggleSidebar, isMobileOpen, closeMobileSidebar } =
    useSidebar();
  const { id: unitId } = useParams<{ id: string }>();
  const { activeTab, setActiveTab } = useNavbarNavigation();

  const handleTabClick = (tabName: string) => {
    setActiveTab(tabName);
    closeMobileSidebar();
  };

  // Check if we're on a unit page (only /units/:id routes, not wagon/bogie/wheel/fleet pages)
  const isUnitPage =
    location.pathname.startsWith("/units/") && unitId !== undefined;
  const navigationItems = isUnitPage ? getUnitNavigationItems(unitId) : [];

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={closeMobileSidebar}
        />
      )}

      {/* Sidebar - Mobile shows for all pages, Desktop only for unit pages */}
      <div
        className={`sidebar fixed left-0 top-[58px] h-[calc(100vh-58px)] z-50 transition-all duration-300 w-[280px]
        ${isMobileOpen ? "translate-x-0" : "-translate-x-full"}
        ${
          isUnitPage
            ? "lg:relative lg:top-0 lg:h-full lg:translate-x-0"
            : "lg:hidden"
        }
        ${isUnitPage && isCollapsed ? "lg:w-14" : "lg:w-[164px]"}`}
        style={{ backgroundColor: "#182836" }}
      >
        <div className="flex flex-col h-full">
          {/* Header with Title and Toggle Button - Desktop only, Unit pages only */}
          {isUnitPage && (
            <div className="hidden lg:flex items-center justify-end p-1.5 border-b border-white/10">
              <button
                onClick={toggleSidebar}
                className="rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-all p-1.5"
                title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              >
                {isCollapsed ? (
                  <ChevronDoubleRightIcon className="w-4 h-4 transition-transform duration-300 ease-in-out" />
                ) : (
                  <ChevronDoubleLeftIcon className="w-4 h-4 transition-transform duration-300 ease-in-out" />
                )}
              </button>
            </div>
          )}

          <div className="flex-1 overflow-y-auto p-4">
            {/* Mobile Header Navigation - Always show on mobile */}
            <div className="lg:hidden mb-6">
              <TenantHeaderTabs
                activeTab={activeTab}
                onTabClick={handleTabClick}
                isMobile={true}
              />
            </div>

            {/* Unit Navigation - Show on unit pages */}
            {isUnitPage && navigationItems.length > 0 && (
              <div className={`${isMobileOpen ? "mb-6 lg:mb-0" : ""}`}>
                <h3 className="text-xs font-semibold text-gray-400 uppercase mb-3 px-2 lg:hidden">
                  {t("unit.navigation.title", "Unit Details")}
                </h3>
                <nav>
                  {navigationItems.map((item) => {
                    const isActive = location.pathname === item.path;
                    const isDisabled = item.disabled;

                    const content = (
                      <>
                        <span className="flex-shrink-0">{item.icon}</span>
                        {!isCollapsed && <span>{t(item.translationKey)}</span>}
                      </>
                    );

                    if (isDisabled) {
                      return (
                        <div
                          key={item.name}
                          className={`flex items-center p-3 gap-2 text-sm font-normal h-[44px] rounded-lg cursor-not-allowed opacity-50 ${
                            isCollapsed ? "justify-center lg:rounded-none" : ""
                          } text-[#6B7280]`}
                        >
                          {content}
                        </div>
                      );
                    }

                    return (
                      <Link
                        key={item.name}
                        to={item.path}
                        onClick={closeMobileSidebar}
                        className={`flex items-center p-3 gap-2 text-sm font-normal h-[44px] rounded-lg ${
                          isCollapsed ? "justify-center lg:rounded-none" : ""
                        } ${
                          isActive
                            ? "bg-[#000000]/24 text-white"
                            : "text-[#CAD5E2] hover:text-white hover:bg-white/10 border border-transparent hover:border-white/20"
                        }`}
                      >
                        {content}
                      </Link>
                    );
                  })}
                </nav>
              </div>
            )}

            {/* Mobile Notifications Section */}
            <div className="lg:hidden sm:hidden border-t border-white/10 pt-4 mt-4">
              <h3 className="text-xs font-semibold text-gray-400 uppercase mb-3 px-2">
                {t("notifications.title", "Notifications")}
              </h3>
              <div className="px-2">
                <NotificationsTrigger />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-white/20">
            {!isCollapsed && (
              <div className="text-xs text-gray-400 text-center">
                <div className="gradient-text font-semibold">DS Insight</div>
                <div className="text-gray-500">v1.0</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
